import React from 'react';
import classNames from 'classnames';

import { Button } from '@components/button';
import { Icon, IconName, IconSize } from '@components/icon';
import { Body, Heading } from '@components/text';

import './ModalContent.scss';

export interface ModalContentProps {
  /** Icon name to display */
  iconName?: IconName;
  /** Size of the icon */
  iconSize?: IconSize;
  /** Modal title text */
  title?: string;
  /** Modal body content, rendered inside Body — plain text or inline nodes */
  content?: React.ReactNode;
  /** Block-level custom content rendered between the body and the footer */
  children?: React.ReactNode;
  /** ID for the title element (for ARIA) */
  titleId?: string;
  /** ID for the content element (for ARIA) */
  contentId?: string;
  /** Whether to show the footer buttons. Default: true */
  hasFooter?: boolean;
  /** Label for confirm button. Default: 'Confirm' */
  confirmLabel?: string;
  /** Label for cancel button. */
  cancelLabel?: string;
  /** Button size. Default: 'large' */
  buttonSize?: 'medium' | 'large';
  /** Callback fired when confirm button is clicked */
  onConfirm?: () => void;
  /** Callback fired when modal should close */
  onClose: () => void;
  /** Additional CSS class */
  className?: string;
  /** Whether to prevent auto tracking. Default: false */
  preventAutoTracking?: boolean;
}

/**
 * ModalContent component for rendering the content inside a Modal.
 *
 * This component handles the presentation of modal content including icon, title, body text,
 * and footer buttons. It is designed to be used within the Modal component but can also be
 * used independently for custom modal implementations.
 *
 * @param props - ModalContent component props
 * @param props.iconName - Icon name to display
 * @param props.iconSize - Size of the icon
 * @param props.title - Modal title text
 * @param props.content - Modal body content, rendered inside Body — plain text or inline nodes
 * @param props.children - Block-level custom content rendered between the body and the footer
 * @param props.titleId - ID for the title element (for ARIA)
 * @param props.contentId - ID for the content element (for ARIA)
 * @param props.hasFooter - Whether to show the footer buttons. Default: true
 * @param props.confirmLabel - Label for confirm button. Default: 'Confirm'
 * @param props.cancelLabel - Label for cancel button.
 * @param props.buttonSize - Button size. Default: 'large'
 * @param props.onConfirm - Callback fired when confirm button is clicked
 * @param props.onClose - Callback fired when modal should close
 * @param props.className - Additional CSS class
 * @param props.preventAutoTracking - Whether to prevent auto tracking. Default: false
 */
export const ModalContent: React.FC<ModalContentProps> = ({
  iconName,
  iconSize = 'large',
  title,
  content,
  children,
  titleId,
  contentId,
  hasFooter = true,
  confirmLabel = 'Confirm',
  cancelLabel,
  buttonSize = 'large',
  onConfirm,
  onClose,
  className,
  preventAutoTracking = false,
}) => {
  const bodyText =
    content || content === 0 ? (
      <Body
        id={contentId}
        size="small"
        weight="regular"
        align="center"
        className="vds-modal-content__body-text">
        {content}
      </Body>
    ) : null;

  return (
    <div className={classNames('vds-modal-content__items', className)}>
      {iconName && <Icon name={iconName} size={iconSize} className="vds-modal-content__icon" />}

      {title ? (
        <div className="vds-modal-content__header">
          <Heading
            id={titleId}
            variant="heading-02"
            weight="semibold"
            ariaLevel={1}
            align="center"
            tabIndex={-1}>
            {title}
          </Heading>
          {bodyText}
        </div>
      ) : (
        bodyText && <div className="vds-modal-content__body">{bodyText}</div>
      )}

      {children}

      {hasFooter && (onConfirm !== undefined || cancelLabel !== undefined) && (
        <div className="vds-modal-content__footer">
          {onConfirm && (
            <Button
              className={classNames('vds-modal-content__button', {
                'vds-modal-content__button--full-width': !cancelLabel,
              })}
              size={buttonSize}
              variant="solid"
              colorScheme="primary"
              onClick={onConfirm}
              {...(preventAutoTracking && {
                'data-prevent-auto-tracking': preventAutoTracking,
              })}>
              {confirmLabel}
            </Button>
          )}

          {cancelLabel && (
            <Button
              className={classNames('vds-modal-content__button', {
                'vds-modal-content__button--full-width': !onConfirm,
              })}
              size={buttonSize}
              variant="solid"
              colorScheme="secondary"
              onClick={onClose}
              {...(preventAutoTracking && {
                'data-prevent-auto-tracking': preventAutoTracking,
              })}>
              {cancelLabel}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
